import { useSyncExternalStore } from 'react';

import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { getQueryClient } from 'app/get-query-client';
import { createApiClient } from 'lib/api/createApiClient';
import { useAuthStore } from 'lib/stores/authStore';
import {
    getUserPositionsWsVersion,
    subscribeToRawPosition,
    useUserPositionsStore,
} from 'lib/stores/userPositionsStore';
import { numberFromFixedPointString } from 'models/fixedPoint';
import type { EventPositionVo, PositionVo } from 'models/index';
import type { GetEventPositionResponse } from 'models/reqres';
import type { PositionDTO } from 'models/ws';
import { fetchAllEventPositions } from 'queries/portfolioEvents';
import { positionDtoToVo } from 'queries/portfolioEventTransform';
import { getSubaccountNumber, useSubAccountStore } from 'stores/zustand/subAccountStore';
import { eventContractWsClient } from 'transport/wsClients';
import config from 'utils/config';

const api = createApiClient({
    baseURL: `${config.exchangeApiUrl}/v1`,
});

export const eventPositionKeys = {
    all: ['eventPosition'] as const,
    // Scoped by subaccount: the `/event_positions` endpoint returns different
    // holdings per active subaccount (the axios interceptor scopes the request),
    // so switching subaccounts must key into a different cache entry — otherwise
    // a mounted event page renders the previous subaccount's positions until a
    // refetch. Matches `positionPickerEvents` keying.
    event: (userId: string, subaccountNumber: number, eventTicker: string) =>
        [...eventPositionKeys.all, userId, subaccountNumber, eventTicker] as const,
};

const POSITION_WS_CHANNEL = 'cdc_qe_user_positions';

// Reactive view of whether the position channel is live, so the query's
// `refetchInterval` recomputes when the socket connects/drops. Without this,
// `refetchInterval` is only re-evaluated after a fetch or re-render: mounting
// while connected installs no timer, and a later disconnect would never start
// the fallback poll. A disconnect fires a connection-state event → re-render →
// interval installs; a reconnect self-heals on the next poll tick (React Query
// re-evaluates `refetchInterval` after each fetch).
function subscribePositionSocketState(onChange: () => void): () => void {
    return eventContractWsClient.subscribeConnectionState(() => onChange());
}
function getPositionSocketConnectedSnapshot(): boolean {
    return eventContractWsClient.isConnected(POSITION_WS_CHANNEL);
}
function getPositionSocketConnectedServerSnapshot(): boolean {
    return false;
}
export function usePositionSocketConnected(): boolean {
    return useSyncExternalStore(
        subscribePositionSocketState,
        getPositionSocketConnectedSnapshot,
        getPositionSocketConnectedServerSnapshot,
    );
}

/**
 * Markets whose cache entry a `user_position` WS delta wrote while the REST
 * request was in flight, identified by a changed entry reference between the
 * pre-request snapshot (`before`) and the post-request cache (`after`).
 * `applyPositionDtoToEventPositionCache` rebuilds only the touched market's
 * entry object (unchanged markets keep their original reference in the copied
 * array) — so a differing reference for a ticker means WS delivered newer data
 * for it than this (possibly pre-fill) REST read.
 */
function wsUpdatedMarketsDuringFlight(
    before: EventPositionVo | null | undefined,
    after: EventPositionVo | null | undefined,
): Map<string, PositionVo> {
    const beforeByTicker = new Map<string, PositionVo>();
    for (const mp of before?.market_positions ?? []) beforeByTicker.set(mp.market_ticker, mp);

    const touched = new Map<string, PositionVo>();
    for (const mp of after?.market_positions ?? []) {
        if (beforeByTicker.get(mp.market_ticker) !== mp) touched.set(mp.market_ticker, mp);
    }
    return touched;
}

/**
 * Merge a fresh REST snapshot over whatever is already in the query cache so a
 * `user_position` WS delta that landed while the REST request was in flight
 * isn't clobbered by the (possibly stale) REST return value.
 *
 * A WS push — e.g. the fill right after placing an order — writes the newer
 * position into this same cache entry via `applyPositionDtoToEventPositionCache`
 * mid-flight; the REST read can still be pre-fill under BE eventual consistency.
 * Any market a WS delta touched during the request (`before`→`after` reference
 * diff) wins over the REST row for that ticker; every other market takes the
 * authoritative REST value. REST never drops cached markets it didn't return
 * (WS owns close-out deletions), so WS-only markets survive too.
 */
function mergeRestSnapshotOverCache(
    before: EventPositionVo | null | undefined,
    after: EventPositionVo | null | undefined,
    incoming: EventPositionVo | null,
): EventPositionVo | null {
    const wsTouched = wsUpdatedMarketsDuringFlight(before, after);

    // REST returned no event position, but WS may have added/updated markets
    // while the request was in flight — keep the cache rather than wiping it.
    if (!incoming) return after ?? null;

    const byTicker = new Map<string, PositionVo>();
    // Seed with cached (incl. WS-applied) markets so WS-only markets survive...
    for (const mp of after?.market_positions ?? []) byTicker.set(mp.market_ticker, mp);
    // ...then apply REST rows, except for markets a WS delta just updated —
    // those cached entries are newer than this REST read and must not regress.
    for (const mp of incoming.market_positions ?? []) {
        if (!wsTouched.has(mp.market_ticker)) byTicker.set(mp.market_ticker, mp);
    }

    return { ...incoming, market_positions: Array.from(byTicker.values()) };
}

export function useEventPositionQuery(eventTicker: string | null): {
    data: EventPositionVo | null | undefined;
    isLoading: boolean;
    isFetched: boolean;
} {
    const userId = useAuthStore((s) => s.userId);
    const isLoggedInMember = useAuthStore((s) => s.isLoggedInMember);
    const subaccountNumber = useSubAccountStore((s) => s.subaccountNumber);
    const isPositionSocketConnected = usePositionSocketConnected();

    return useQuery({
        queryKey: eventPositionKeys.event(userId ?? '', subaccountNumber, eventTicker ?? ''),
        queryFn: async ({ signal, queryKey }) => {
            try {
                const queryClient = getQueryClient();
                // Snapshot the cache and the store's WS-write version before the
                // request so the merge (query cache) and hydrate (Zustand store)
                // below can both tell which markets a WS delta touched while it
                // was in flight — those must not be overwritten by an older REST
                // row (e.g. a fill that lands before this pre-fill snapshot does).
                const before = queryClient.getQueryData<EventPositionVo | null>(queryKey);
                const knownWsVersion = getUserPositionsWsVersion();
                const response = await api.get<GetEventPositionResponse>(
                    `/users/${userId}/event_positions/${eventTicker}`,
                    { signal },
                );
                // Hydrate the Zustand store from REST (the order panel reads it
                // directly) but never delete via REST — WS owns close-out
                // deletions — and keep any market a WS delta updated mid-flight.
                const eventPosition = response.data.event_position;
                if (eventPosition) {
                    useUserPositionsStore.getState().actions.hydrate([eventPosition], { knownWsVersion });
                }
                // Don't return the raw REST value — merge it over what's in the
                // cache now so WS deltas applied while this request was in flight
                // survive (the event selectors render from this cache, not the
                // Zustand store). Read the cache last, with no `await` before the
                // return, so no WS push can slip in between.
                const after = queryClient.getQueryData<EventPositionVo | null>(queryKey);
                return mergeRestSnapshotOverCache(before, after, eventPosition);
            } catch (error) {
                if (isAxiosError(error) && error.response?.status === 404) return null;
                throw error;
            }
        },
        enabled: isLoggedInMember && !!eventTicker,
        // WS keeps the cached position live; the interval is only a fallback
        // while the position channel is down (mirrors the slow-polling
        // fallback used when its socket wasn't connected). Driven by the
        // reactive `isPositionSocketConnected` so a socket drop restarts
        // polling instead of leaving the query without a timer.
        refetchInterval: isPositionSocketConnected ? false : config.pollingInterval,
    });
}

/**
 * Count of events the user currently holds an open position in (any market
 * position still open in a market that hasn't closed). Fetches fresh on each
 * mount — used by the account-deactivation dialog's advisory position row.
 */
export function useOpenEventPositionsCount(): { count: number; isLoading: boolean } {
    const userId = useAuthStore((s) => s.userId);
    const isLoggedInMember = useAuthStore((s) => s.isLoggedInMember);

    const { data, isLoading } = useQuery({
        queryKey: [...eventPositionKeys.all, 'openCount', userId ?? ''],
        queryFn: async ({ signal }) => {
            const eventPositions = await fetchAllEventPositions(userId ?? '', { position_status: 'open' }, signal);
            return eventPositions.filter((ep) =>
                ep.market_positions.some(
                    (mp) => Math.abs(numberFromFixedPointString(mp.position_fp)) > 0 && !mp.is_closed,
                ),
            ).length;
        },
        enabled: isLoggedInMember && !!userId,
        staleTime: 0,
    });

    return { count: data ?? 0, isLoading };
}

/**
 * Re-fetch the cached event position for `eventTicker` (imperative, for
 * non-React callers — e.g. the orders store after a fill, or the trading-sync
 * reconnect path). Active `useEventPositionQuery` subscribers refetch
 * immediately; inactive caches are just marked stale.
 */
export function refreshEventPosition(eventTicker: string): void {
    const userId = useAuthStore.getState().userId;
    if (!userId || !eventTicker) return;
    void getQueryClient().invalidateQueries({
        queryKey: eventPositionKeys.event(userId, getSubaccountNumber(), eventTicker),
    });
}

/**
 * Apply a `user_position` WS delta to the per-event query cache so mounted
 * `useEventPositionQuery` consumers tick without a refetch. Events with no
 * query at all are skipped — positions fetch on demand.
 */
function applyPositionDtoToEventPositionCache(dto: PositionDTO): void {
    const userId = useAuthStore.getState().userId;
    if (!userId || !dto.event_ticker || !dto.market_ticker) return;

    const queryClient = getQueryClient();
    // The WS fanout only delivers active-subaccount payloads (filtered in
    // `handleUserPositionMessages`), so key into the active subaccount's cache.
    const key = eventPositionKeys.event(userId, getSubaccountNumber(), dto.event_ticker);
    const existing = queryClient.getQueryData<EventPositionVo | null>(key);
    // `undefined` means no data cached yet. If a query for this event is already
    // active/in-flight (initial event-page load), seed it below so a fill that
    // arrives before the first REST response isn't dropped — the queryFn folds
    // its own snapshot over this on arrival. If no query exists at all, skip:
    // don't create a phantom cache entry for an event nobody is viewing.
    if (existing === undefined && !queryClient.getQueryState(key)) return;

    const positionVo = positionDtoToVo(dto);
    // `!existing` covers both an in-flight query with no data yet (`undefined`)
    // and one that fetched empty (`null`) — seed a fresh event position.
    if (!existing) {
        queryClient.setQueryData<EventPositionVo>(key, {
            user_id: dto.user_id,
            event_ticker: dto.event_ticker,
            collateral_returned_cents: 0,
            last_updated_ts: '',
            market_positions: [positionVo],
        });
        return;
    }

    const marketPositions = existing.market_positions ?? [];
    const idx = marketPositions.findIndex((mp) => mp.market_ticker === dto.market_ticker);
    const next = [...marketPositions];
    if (idx === -1) {
        next.push(positionVo);
    } else {
        // The DTO doesn't carry `is_closed` or `last_updated_ts`; keep the
        // REST-sourced values rather than the stub projections.
        next[idx] = {
            ...positionVo,
            is_closed: marketPositions[idx].is_closed,
            last_updated_ts: marketPositions[idx].last_updated_ts,
        };
    }
    queryClient.setQueryData<EventPositionVo>(key, { ...existing, market_positions: next });
}

if (typeof window !== 'undefined') {
    subscribeToRawPosition(applyPositionDtoToEventPositionCache);

    // Drop every cached event position across an auth boundary (logout, or a
    // different account logging in on the same browser). Otherwise a fast
    // re-login within React Query's stale window would keep rendering the
    // previous session's holdings — the reconnected socket disables the
    // fallback poll, so nothing would refetch until a WS delta arrives. The
    // Zustand live map is cleared separately in `userPositionsStore`'s own auth
    // subscription. Guarded like the other module-level auth subscribers so
    // tests that mock `useAuthStore` without `subscribe` don't crash on import.
    if (typeof useAuthStore.subscribe === 'function') {
        useAuthStore.subscribe((state, prev) => {
            if (state.userId === prev.userId) return;
            getQueryClient().removeQueries({ queryKey: eventPositionKeys.all });
        });
    }
}
